/**
 * `/ajuda` — a Central de ajuda: perguntas frequentes e o chamado ao
 * suporte (2026-10-15; redesenho de 2026-10-20).
 *
 * O chamado vai para o helpdesk do suporte (Zammad) PELA API: o navegador
 * nunca fala com o Zammad, porque o token dele abriria chamado de qualquer
 * pessoa. Decisões da dona: só ida (a resposta chega por e-mail e pelo
 * portal do helpdesk, nada volta ao sino) e, em produção, o helpdesk mora
 * em outro domínio. É por isso que o link de acompanhamento vem da API
 * (`portal_url`) e não de variável do front.
 *
 * Sem helpdesk no ambiente a página continua útil: o FAQ aparece e o
 * bloco do suporte diz que o chamado não está ligado ali, em vez de um
 * formulário que responderia 503.
 *
 * O redesenho de 2026-10-20, pela usabilidade:
 *
 * - em tela larga o FAQ e o chamado ficam LADO A LADO (o chamado fixo ao
 *   rolar); antes as nove perguntas empurravam o formulário pra baixo da
 *   dobra;
 * - o FAQ tem busca, e a busca sem resultado leva o termo pro chamado;
 * - o assunto é escolhido em quatro cartões (era um select escondido), e a
 *   dica da descrição muda com ele;
 * - o botão desabilitado diz o que falta; Ctrl+Enter envia;
 * - "o que vai junto" mostra os dados de verdade, não só a lista;
 * - o rascunho sobrevive a sair da tela (sessionStorage, por aba);
 * - aberto o chamado, o formulário dá lugar à confirmação, com o portal
 *   (onde o acompanhamento acontece) como ação principal. O link "Meus
 *   chamados" fica no cabeçalho do cartão o tempo todo.
 *
 * Anexos (2026-10-20): imagem, PDF e planilha, arrastados, escolhidos ou
 * colados com Ctrl+V em qualquer ponto do formulário. Os limites vêm da
 * API; os arquivos NÃO entram no rascunho (o storage guarda texto).
 */

import {
  Bug,
  CheckCircle2,
  ChevronDown,
  CircleHelp,
  ExternalLink,
  KeyRound,
  Lightbulb,
  LifeBuoy,
  LoaderCircle,
  MessageCircleQuestion,
  Search,
  Send,
  X,
  type LucideIcon
} from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent,
  type FormEvent,
  type KeyboardEvent
} from "react";
import { useLocation } from "react-router-dom";
import { apiClient, type SupportStatus, type SupportTicket } from "@/shared/api/api-client";
import { errorMessage, lastSupportCode } from "@/shared/api/http";
import { ApiState } from "@/shared/components/ApiState";
import { ROLE_LABEL, ROLES } from "@/shared/utils/roles";
import { useAppShell } from "@/shell/app-shell-context";
import { HELP_FAQ } from "./help-faq";
import { SupportAttachments } from "./SupportAttachments";
import { addAttachments, pastedFiles, type AttachmentLimits } from "./support-attachments";

/** Os mesmos limites que o servidor confere (`application/support.py`). */
const SUBJECT_MIN = 3;
const SUBJECT_MAX = 150;
const DESCRIPTION_MIN = 10;
const DESCRIPTION_MAX = 5000;

/** Ícone e dica por assunto. As chaves são as do servidor
 *  (`support.CATEGORIES`); assunto novo lá aparece com o ícone genérico. */
const CATEGORY_UI: Record<string, { icon: LucideIcon; hint: string }> = {
  duvida: { icon: CircleHelp, hint: "O que você quer fazer e em que ponto travou." },
  problema: { icon: Bug, hint: "O que você fez, o que esperava e o que viu na tela." },
  acesso: { icon: KeyRound, hint: "Qual empresa ou tela, e o que aparece quando você tenta abrir." },
  sugestao: { icon: Lightbulb, hint: "O que você gostaria que existisse e como isso ajudaria." }
};
const DEFAULT_HINT = "Escolha um assunto e conte o que aconteceu.";

/** Rascunho por aba: some ao fechar a aba, como o resto da sessão. */
const DRAFT_KEY = "reputation_support_draft";
type Draft = { category: string; subject: string; description: string };
const EMPTY_DRAFT: Draft = { category: "", subject: "", description: "" };

function readDraft(): Draft {
  try {
    const raw = JSON.parse(sessionStorage.getItem(DRAFT_KEY) ?? "null") as Partial<Draft> | null;
    if (!raw) return EMPTY_DRAFT;
    return {
      category: typeof raw.category === "string" ? raw.category : "",
      subject: typeof raw.subject === "string" ? raw.subject : "",
      description: typeof raw.description === "string" ? raw.description : ""
    };
  } catch {
    return EMPTY_DRAFT;
  }
}

function writeDraft(draft: Draft) {
  try {
    if (!draft.category && !draft.subject && !draft.description) sessionStorage.removeItem(DRAFT_KEY);
    else sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch {
    // Sem storage (aba anônima, bloqueio) o rascunho só não sobrevive.
  }
}

/** "há 3 min" — o código só ajuda se a pessoa reconhecer de qual erro é. */
function minutesAgo(at: number): string {
  const minutes = Math.round((Date.now() - at) / 60000);
  if (minutes < 1) return "agora há pouco";
  if (minutes < 60) return `há ${minutes} min`;
  return `há ${Math.round(minutes / 60)} h`;
}

/** Busca sem acento e sem caixa: "relatorio" acha "Relatório". */
function fold(text: string): string {
  return text.normalize("NFD").replace(/\p{Diacritic}/gu, "").toLowerCase();
}

function joinMissing(items: string[]): string {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} e ${items.at(-1)}`;
}

export function HelpCenterPage() {
  const { token, profile, company } = useAppShell();
  // A tela onde a pessoa estava quando abriu a ajuda (o menu da conta
  // manda no `state` do link). Abrir `/ajuda` direto não tem origem.
  const location = useLocation();
  const fromPage = (location.state as { from?: string } | null)?.from ?? null;
  const recentCode = useMemo(() => lastSupportCode(), []);
  const ids = useId();

  const [status, setStatus] = useState<SupportStatus | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [draft, setDraft] = useState<Draft>(readDraft);
  const [attachCode, setAttachCode] = useState(true);
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sent, setSent] = useState<SupportTicket | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState<string | null>(null);

  const subjectRef = useRef<HTMLInputElement>(null);
  const supportRef = useRef<HTMLElement>(null);
  const doneRef = useRef<HTMLHeadingElement>(null);

  const load = useCallback(async () => {
    setLoadError(null);
    try {
      setStatus(await apiClient.getSupportStatus(token));
    } catch (cause) {
      setLoadError(errorMessage(cause, "Não foi possível saber se o suporte está disponível."));
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    writeDraft(draft);
  }, [draft]);

  // A confirmação recebe o foco: quem usa leitor de tela ouve o número do
  // chamado, e quem usa teclado não fica num botão que sumiu.
  useEffect(() => {
    if (sent) doneRef.current?.focus({ preventScroll: true });
  }, [sent]);

  const faq = useMemo(() => {
    const needle = fold(query.trim());
    if (!needle) return HELP_FAQ;
    return HELP_FAQ.filter(entry => fold(`${entry.question} ${entry.answer}`).includes(needle));
  }, [query]);

  const update = (patch: Partial<Draft>) => setDraft(current => ({ ...current, ...patch }));

  const subject = draft.subject.trim();
  const description = draft.description.trim();
  const missing = [
    ...(draft.category === "" ? ["o assunto"] : []),
    ...(subject.length < SUBJECT_MIN ? ["o título"] : []),
    ...(description.length < DESCRIPTION_MIN ? ["a descrição"] : [])
  ];
  const canSend = missing.length === 0 && !sending;
  const descriptionShort = description.length > 0 && description.length < DESCRIPTION_MIN;
  const hint = CATEGORY_UI[draft.category]?.hint ?? DEFAULT_HINT;
  const role = ROLES.find(item => profile.roles.includes(item));

  /** Da busca sem resposta pro chamado: o termo vira o título (se ainda
   *  não houver um) e o foco vai pro formulário. */
  const askSupport = () => {
    if (!draft.subject.trim() && query.trim()) update({ subject: query.trim().slice(0, SUBJECT_MAX) });
    setSent(null);
    supportRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    // Depois do re-render, quando o formulário já voltou a existir.
    window.setTimeout(() => subjectRef.current?.focus(), 0);
  };

  const limits: AttachmentLimits | null = status?.available
    ? {
        max: status.max_attachments,
        maxBytes: status.attachment_max_bytes,
        extensions: status.attachment_extensions
      }
    : null;

  const addFiles = (incoming: File[]) => {
    if (!limits || incoming.length === 0) return;
    const result = addAttachments(files, incoming, limits);
    setFiles(result.files);
    setFileError(result.error);
  };

  const removeFile = (index: number) => {
    setFiles(current => current.filter((_, position) => position !== index));
    setFileError(null);
  };

  /** Ctrl+V com arquivo (o print) vira anexo; colar texto segue normal. */
  const onFormPaste = (event: ClipboardEvent<HTMLFormElement>) => {
    const pasted = Array.from(event.clipboardData?.files ?? []);
    if (pasted.length === 0) return;
    event.preventDefault();
    addFiles(pastedFiles(pasted, new Date()));
  };

  const submit = async (event?: FormEvent) => {
    event?.preventDefault();
    if (!canSend) return;
    setSending(true);
    setSendError(null);
    try {
      const ticket = await apiClient.openSupportTicket(token, {
        category: draft.category,
        subject,
        description,
        page: fromPage,
        support_code: attachCode && recentCode ? recentCode.code : null
      }, files);
      setSent(ticket);
      setDraft(EMPTY_DRAFT);
      setFiles([]);
      setFileError(null);
    } catch (cause) {
      setSendError(errorMessage(cause, "Não foi possível abrir o chamado."));
    } finally {
      setSending(false);
    }
  };

  const onFormKeyDown = (event: KeyboardEvent<HTMLFormElement>) => {
    if (event.key === "Enter" && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      void submit();
    }
  };

  return (
    <div className="help-center">
      <section className="card account-card help-faq-card" aria-labelledby={`${ids}-faq`}>
        <header className="help-card-header">
          <span className="help-card-icon" aria-hidden="true">
            <MessageCircleQuestion size={18} />
          </span>
          <div>
            <h2 id={`${ids}-faq`}>Perguntas frequentes</h2>
            <p>As dúvidas que mais chegam ao suporte, com a resposta curta.</p>
          </div>
        </header>

        <div className="help-search">
          <Search size={16} aria-hidden="true" />
          <input
            type="search"
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder="Buscar nas perguntas (ex.: amostra, upload, senha)"
            aria-label="Buscar nas perguntas frequentes"
          />
          {query && (
            <button type="button" className="help-search-clear" onClick={() => setQuery("")} aria-label="Limpar busca">
              <X size={14} />
            </button>
          )}
        </div>

        {faq.length > 0 ? (
          <div className="help-faq">
            {faq.map(entry => (
              <details key={entry.question} className="help-faq-item">
                <summary>
                  <span>{entry.question}</span>
                  <ChevronDown size={16} className="help-faq-chevron" aria-hidden="true" />
                </summary>
                <p>{entry.answer}</p>
              </details>
            ))}
          </div>
        ) : (
          <div className="help-faq-empty" role="status">
            <p>Nenhuma pergunta fala de “{query.trim()}”.</p>
            {status?.available && (
              <button type="button" className="secondary-button" onClick={askSupport}>
                <LifeBuoy size={15} />
                Perguntar ao suporte
              </button>
            )}
          </div>
        )}
      </section>

      <section
        ref={supportRef}
        className="card account-card help-support-card"
        aria-labelledby={`${ids}-support`}
      >
        <header className="help-card-header">
          <span className="help-card-icon" aria-hidden="true">
            <LifeBuoy size={18} />
          </span>
          <div>
            <h2 id={`${ids}-support`}>Falar com o suporte</h2>
            <p>
              Não achou a resposta? Abra um chamado. A resposta chega no seu
              e-mail{profile.email ? ` (${profile.email})` : ""}.
            </p>
          </div>
          {status?.available && status.portal_url && (
            <a className="help-portal-link" href={status.portal_url} target="_blank" rel="noreferrer">
              Meus chamados <ExternalLink size={13} aria-hidden="true" />
            </a>
          )}
        </header>

        {loadError ? (
          <ApiState kind="error" onRetry={() => void load()}>
            {loadError}
          </ApiState>
        ) : status == null ? (
          <ApiState kind="loading">Verificando o suporte…</ApiState>
        ) : !status.available ? (
          <p className="help-unavailable">
            O suporte por chamado não está ligado neste ambiente. Fale com a
            equipe responsável pelo ReputAItion.
          </p>
        ) : sent ? (
          <div className="help-ticket-done" role="status">
            <span className="help-ticket-done-icon" aria-hidden="true">
              <CheckCircle2 size={26} />
            </span>
            <h3 ref={doneRef} tabIndex={-1}>
              Chamado #{sent.number} aberto
            </h3>
            <p>
              O suporte responde no seu e-mail. Respostas, andamento e
              encerramento ficam no portal do suporte.
            </p>
            <div className="help-ticket-done-actions">
              <a className="primary-button" href={sent.portal_url} target="_blank" rel="noreferrer">
                Acompanhar no portal do suporte <ExternalLink size={14} aria-hidden="true" />
              </a>
              <button type="button" className="secondary-button" onClick={() => setSent(null)}>
                Abrir outro chamado
              </button>
            </div>
          </div>
        ) : (
          <form className="help-ticket-form" onSubmit={event => void submit(event)} onKeyDown={onFormKeyDown} onPaste={onFormPaste}>
            <fieldset className="help-categories">
              <legend>Assunto</legend>
              <div className="help-category-grid">
                {Object.entries(status.categories).map(([value, label]) => {
                  const Icon = CATEGORY_UI[value]?.icon ?? MessageCircleQuestion;
                  const selected = draft.category === value;
                  return (
                    <label key={value} className={`help-category${selected ? " is-selected" : ""}`}>
                      <input
                        type="radio"
                        name={`${ids}-category`}
                        value={value}
                        checked={selected}
                        onChange={() => update({ category: value })}
                      />
                      <Icon size={17} aria-hidden="true" />
                      <span>{label}</span>
                    </label>
                  );
                })}
              </div>
            </fieldset>

            <div className="form-field">
              <div className="help-field-head">
                <label htmlFor={`${ids}-subject`}>Título</label>
                <span className="help-counter" id={`${ids}-subject-count`}>
                  {draft.subject.length}/{SUBJECT_MAX}
                </span>
              </div>
              <input
                id={`${ids}-subject`}
                ref={subjectRef}
                value={draft.subject}
                maxLength={SUBJECT_MAX}
                placeholder="Ex.: a amostra de março não termina de gerar"
                aria-describedby={`${ids}-subject-count`}
                onChange={event => update({ subject: event.target.value })}
                required
              />
            </div>

            <div className="form-field">
              <div className="help-field-head">
                <label htmlFor={`${ids}-description`}>Descrição</label>
                <span
                  className={`help-counter${descriptionShort ? " is-short" : ""}`}
                  id={`${ids}-description-count`}
                >
                  {descriptionShort
                    ? `mais ${DESCRIPTION_MIN - description.length} caracteres`
                    : `${draft.description.length}/${DESCRIPTION_MAX}`}
                </span>
              </div>
              <textarea
                id={`${ids}-description`}
                value={draft.description}
                rows={7}
                maxLength={DESCRIPTION_MAX}
                aria-describedby={`${ids}-description-hint ${ids}-description-count`}
                onChange={event => update({ description: event.target.value })}
                required
              />
              <small id={`${ids}-description-hint`}>{hint}</small>
            </div>

            {limits && (
              <SupportAttachments
                files={files}
                limits={limits}
                error={fileError}
                onAdd={addFiles}
                onRemove={removeFile}
              />
            )}

            {recentCode && (
              <label className="help-attach-code">
                <input
                  type="checkbox"
                  checked={attachCode}
                  onChange={event => setAttachCode(event.target.checked)}
                />
                <span>
                  Anexar o código do último erro
                  <small>
                    <code>{recentCode.code}</code> · {minutesAgo(recentCode.at)}
                  </small>
                </span>
              </label>
            )}

            <details className="help-context">
              <summary>
                <ChevronDown size={14} className="help-faq-chevron" aria-hidden="true" />
                O que vai junto com o chamado
              </summary>
              <p>Para o suporte não precisar perguntar de volta:</p>
              <dl>
                <dt>Você</dt>
                <dd>
                  {profile.name || "—"}
                  {profile.email ? ` · ${profile.email}` : ""}
                </dd>
                <dt>Papel</dt>
                <dd>{role ? ROLE_LABEL[role] : "—"}</dd>
                <dt>Empresa aberta</dt>
                <dd>{company?.name ?? "nenhuma"}</dd>
                {fromPage && (
                  <>
                    <dt>Tela de origem</dt>
                    <dd>
                      <code>{fromPage}</code>
                    </dd>
                  </>
                )}
                <dt>Navegador</dt>
                <dd>o nome e a versão</dd>
              </dl>
            </details>

            {sendError && <ApiState kind="error">{sendError}</ApiState>}

            <div className="help-submit-row">
              <small className="help-submit-hint" aria-live="polite">
                {missing.length > 0 ? (
                  <>Falta preencher {joinMissing(missing)}.</>
                ) : (
                  <>
                    Pronto para enviar · <kbd>Ctrl</kbd> + <kbd>Enter</kbd>
                  </>
                )}
              </small>
              <button type="submit" className="primary-button" disabled={!canSend}>
                {sending ? <LoaderCircle size={16} className="spin" /> : <Send size={16} />}
                {sending ? "Enviando…" : "Abrir chamado"}
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}
